/* eslint-disable react/no-array-index-key */
import React, { useEffect, useRef, useState } from 'react';
import {
  useSelector,
  useDispatch,
} from 'react-redux';
import Shimmer from 'react-shimmer-effect';
import styled from '@emotion/styled';
import { TransitionGlobal } from '../../../../shared/GlobalStyles';
import Text from '../../../../shared/components/Text/Text';
import { setNonDateBasedProgramDays, setSelectedProgramDayNumber } from '../../ducks/calendarActions';
import GroupByDaySideBarContent from './GroupByDaySideBarContent';

const Wrapper = styled('div')`
  display: flex;
  flex: 1;
  gap: 8px;
  flex-direction: column;
  background: white;
  overflow: auto;
`;

const ShimmerBlock = styled('div')`
  display: flex;
  width: 100%;
  min-height: 32px;
`;

const WeekButtonWrapper = styled('div')`
  display: flex;
  width: 100%;
  padding: 4px;
  justify-content: space-between;
  :hover {
    background: ${(props) => props.viewType === 'weekView' && '#F8F8F8'};
    cursor: ${(props) => props.viewType === 'weekView' && 'pointer'};
  }
`;

const DayButtonWrapper = styled('div')`
  display: flex;
  width: 100%;
  padding-left: 8px;
  padding-right: 8px;
  justify-content: space-between;
  :hover {
        background: ${(props) => props.viewType === 'dayView' && '#F8F8F8'};
    cursor: ${(props) => props.viewType === 'dayView' && 'pointer'};
  }
`;
const OpacityWrapper = styled('div')`
display: flex;
 transition: ${TransitionGlobal};
    :hover {
      opacity: 0.7;
    }
`;

const ProgramLibrarySideBarContent = () => {
  const dispatch = useDispatch();
  const prevSelectedProgramRef = useRef(null);
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const nonDateBasedProgramItems = useSelector(
    (state) => state.calendar.data.nonDateBasedProgramItems,
  );
  const selectedReduxNonDateBasedProgram = useSelector(
    (state) => state.calendar.data.selectedReduxNonDateBasedProgram,
  );
  const groupByFilter = useSelector(
    (state) => state.calendar.data.groupByFilter,
  );
  const isSavedItemsLoading = useSelector((state) => state.calendar.ui.isSavedItemsLoading);
  const shimmerCount = [1, 2, 3, 4, 5, 6, 7, 8];

  const [programChanged, setProgramChanged] = useState(false);

  /** Detect if program changed */
  useEffect(() => {
    const prev = prevSelectedProgramRef.current;

    if (prev !== selectedReduxNonDateBasedProgram) {
      setProgramChanged(true);
    }

    prevSelectedProgramRef.current = selectedReduxNonDateBasedProgram;
  }, [selectedReduxNonDateBasedProgram]);

  useEffect(() => {
    if (!isSavedItemsLoading) {
      setProgramChanged(false);
    }
  }, [isSavedItemsLoading]);

  /** Step 1: build a grouped array manually using Object.values + find */
  const groupedWeeks = [];

  Object.values(nonDateBasedProgramItems || {}).forEach((item) => {
    const existingWeek = groupedWeeks.find((w) => w.weekNumber === item.weekNumber);

    if (existingWeek) {
      existingWeek.days.push(item);
    } else {
      groupedWeeks.push({
        weekNumber: item.weekNumber,
        days: [item],
      });
    }
  });

  /** Step 2: sort days in each week */
  const sortedGroupedWeeks = groupedWeeks.map((week) => ({
    ...week,
    days: [...week.days].sort((a, b) => a.dayInWeek - b.dayInWeek),
  }));

  const handleDayButtonClick = (dayNumber) => {
    if (viewType === 'dayView') {
      dispatch(setSelectedProgramDayNumber(dayNumber));
    }
  };

  const handleWeekButtonClick = (week) => {
    if (viewType === 'weekView') {
      const firstDayInClickedWeek = week.days.filter((day) => day.dayInWeek === 1);
      const { dayNumber } = firstDayInClickedWeek[0];
      dispatch(setSelectedProgramDayNumber(dayNumber));
    }
  };

  const handleAddDay = (week) => {
    if (!week?.days) return;

    /** 1 Determine the last day in the week */
    const lastDayInWeek = week.days[week.days.length - 1];

    /** 2 Create the new day */
    const newDay = {
      dayInWeek: lastDayInWeek.dayInWeek + 1,
      dayNumber: lastDayInWeek.dayNumber + 1,
      exercises: [],
      repCount: 0,
      setCount: 0,
      weekNumber: week.weekNumber,
      isLocalItem: true,
    };

    /** 3 Copy existing dictionary */
    const updatedItems = { ...nonDateBasedProgramItems };

    /** 4 Add the new day keyed by dayNumber */
    updatedItems[newDay.dayNumber] = newDay;

    /** 5 Update Redux */
    dispatch(setNonDateBasedProgramDays(updatedItems));
  };

  return (
    <Wrapper>
      {isSavedItemsLoading && programChanged ? shimmerCount.map((_, i) => (
        <Shimmer key={i}>
          <ShimmerBlock />
        </Shimmer>
      )) : (
        <>
          {groupByFilter === 'Day' ? (
            <GroupByDaySideBarContent />
          ) : (
            <>
              {sortedGroupedWeeks.map((week) => (
                <React.Fragment key={week.weekNumber}>
                  <WeekButtonWrapper
                    style={{
                      backgroundColor:
              viewType === 'weekView'
              && week.days.some((day) => day.dayNumber === selectedProgramDayNumber)
                ? '#F8F8F8'
                : undefined,
                    }}
                    viewType={viewType}
                    onClick={() => handleWeekButtonClick(week)}
                  >
                    <Text fontSize='16px' fontWeight='700'>{`Week ${week.weekNumber}`}</Text>
                    <OpacityWrapper>
                      <Text
                        fontSize='16px'
                        color='#77A7FF'
                        style={{
                          cursor: week?.days?.length >= 7 ? 'not-allowed' : 'pointer',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (week?.days?.length < 7) {
                            handleAddDay(week);
                          }
                        }}
                      >
                        + Add Day
                      </Text>
                    </OpacityWrapper>
                  </WeekButtonWrapper>
                  {week.days.map((day) => (
                    <DayButtonWrapper
                      style={{ backgroundColor: selectedProgramDayNumber === day.dayNumber && viewType === 'dayView' && '#F8F8F8' }}
                      key={day.dayNumber}
                      viewType={viewType}
                      onClick={() => handleDayButtonClick(day.dayNumber)}
                    >
                      <Text fontSize='16px'>
                        {`Day ${day.dayInWeek}`}
                      </Text>
                      <OpacityWrapper>
                        <Text
                          fontSize='16px'
                          color='#9E9E9E'
                        >
                          {day.exercises?.length || 0}
                          {' '}
                          Items
                        </Text>
                      </OpacityWrapper>
                    </DayButtonWrapper>
                  ))}
                </React.Fragment>
              ))}
            </>
          )}
        </>
      )}
    </Wrapper>
  );
};

export default ProgramLibrarySideBarContent;
